import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Progress } from '@/components/ui/progress';

export default function WhoopCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [syncStatus, setSyncStatus] = useState<'connecting' | 'syncing' | 'done' | 'error'>('connecting');
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('Connecting your WHOOP account...');

  useEffect(() => {
    const handleCallback = async () => {
      const whoopStatus = searchParams.get('whoop');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        const message = errorDescription || searchParams.get('message') || 'Connection failed';
        console.error('WHOOP OAuth Error:', { error, errorDescription, message });
        setSyncStatus('error');
        setSyncMessage(`Connection failed: ${message}`);
        toast.error(`WHOOP Error: ${message}`, { duration: 10000 });
        
        // Redirect back to whoop dashboard after showing error
        setTimeout(() => navigate('/whoop-dashboard'), 3000);
        return;
      }

      if (whoopStatus === 'connected') {
        setSyncStatus('syncing');
        setSyncProgress(10);
        setSyncMessage('Connected! Now importing your complete WHOOP history...');
        toast.success('WHOOP connected! Importing your history...');
        
        // Trigger full historical sync for new connections
        try {
          setSyncProgress(20);
          setSyncMessage('Fetching all your recovery data...');
          
          const { data, error: syncError } = await supabase.functions.invoke('sync-whoop-historical');
          
          if (syncError) {
            console.error('Historical sync error:', syncError);
            setSyncProgress(50);
            setSyncMessage('Falling back to recent data sync...');
            
            // Fall back to regular sync if historical fails
            await supabase.functions.invoke('sync-whoop-data');
            toast.success('Connected and synced recent data!');
          } else {
            setSyncProgress(90);
            const daysImported = data?.daysImported || 0;
            const workoutsImported = data?.workoutsImported || 0;
            setSyncMessage(`Imported ${daysImported} days of data and ${workoutsImported} workouts!`);
            
            toast.success(
              `Imported ${daysImported} days of WHOOP history!`, 
              { description: `Including ${workoutsImported} workouts` }
            );
          }
          
          setSyncProgress(100);
          setSyncStatus('done');
          setSyncMessage('All done! Redirecting to your dashboard...');
          
          // Give user a moment to see the success message
          setTimeout(() => navigate('/whoop-dashboard'), 2000);
        } catch (syncError) {
          console.error('Sync error:', syncError);
          setSyncStatus('error');
          setSyncMessage('Connected but import failed - you can try syncing from the dashboard');
          toast.error('Connected but import failed - please click Sync Now');
          setTimeout(() => navigate('/whoop-dashboard'), 3000);
        }
        
        return;
      }

      // If no status, redirect after a moment
      setTimeout(() => navigate('/whoop-dashboard'), 1000);
    };

    // Add timeout fallback in case something hangs
    const timeoutId = setTimeout(() => {
      console.warn('Callback timeout - redirecting to whoop dashboard');
      navigate('/whoop-dashboard');
    }, 120000); // 2 minute timeout for large historical imports

    handleCallback().finally(() => clearTimeout(timeoutId));

    return () => clearTimeout(timeoutId);
  }, [searchParams, navigate]);

  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted/20">
      <div className="text-center max-w-md w-full">
        {syncStatus !== 'error' && syncStatus !== 'done' && (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        )}
        
        {syncStatus === 'done' && (
          <div className="h-12 w-12 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        
        {syncStatus === 'error' && (
          <div className="h-12 w-12 mx-auto mb-4 rounded-full bg-destructive/20 flex items-center justify-center">
            <svg className="h-6 w-6 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )}
        
        <p className="text-foreground font-medium mb-2">{syncMessage}</p>
        
        {syncStatus === 'syncing' && (
          <div className="mt-4 space-y-2">
            <Progress value={syncProgress} className="h-2" />
            <p className="text-xs text-muted-foreground">
              This may take a minute for accounts with lots of history...
            </p>
          </div>
        )}
        
        {error && (
          <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive font-medium">Error: {error}</p>
            {errorDescription && (
              <p className="text-xs text-muted-foreground mt-1">{errorDescription}</p>
            )}
            <p className="text-xs text-muted-foreground mt-2">Redirecting back...</p>
          </div>
        )}
      </div>
    </div>
  );
}
